import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail, imageUrl } from "@/api";
import { toast } from "sonner";
import { RotateCw, RotateCcw, FlipHorizontal, Crop, Undo2 } from "lucide-react";

const MAX_PREVIEW = 520;

// draws the source image into an offscreen canvas applying rotation + flip
const bake = (img, rotation, flip) => {
  const swap = rotation % 180 !== 0;
  const w = swap ? img.naturalHeight : img.naturalWidth;
  const h = swap ? img.naturalWidth : img.naturalHeight;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d");
  ctx.translate(w / 2, h / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(flip ? -1 : 1, 1);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return c;
};

export const ImageEditor = ({ open, onOpenChange, path, onSaved }) => {
  const [img, setImg] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [flip, setFlip] = useState(false);
  const [crop, setCrop] = useState(null); // {x,y,w,h} in preview px
  const [drag, setDrag] = useState(null);
  const [saving, setSaving] = useState(false);
  const boxRef = useRef(null);
  const canvasRef = useRef(null);
  const bakedRef = useRef(null);

  useEffect(() => {
    if (!open || !path) return;
    setRotation(0);
    setFlip(false);
    setCrop(null);
    const el = new Image();
    el.crossOrigin = "anonymous";
    el.onload = () => setImg(el);
    el.onerror = () => toast.error("Could not load this photo for editing");
    el.src = imageUrl(path);
  }, [open, path]);

  const draw = useCallback(() => {
    if (!img || !canvasRef.current) return;
    const baked = bake(img, rotation, flip);
    bakedRef.current = baked;
    const scale = Math.min(1, MAX_PREVIEW / baked.width);
    const canvas = canvasRef.current;
    canvas.width = Math.round(baked.width * scale);
    canvas.height = Math.round(baked.height * scale);
    canvas.getContext("2d").drawImage(baked, 0, 0, canvas.width, canvas.height);
  }, [img, rotation, flip]);

  useEffect(() => {
    draw();
    setCrop(null);
  }, [draw]);

  const point = (e) => {
    const r = boxRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return {
      x: Math.max(0, Math.min(r.width, t.clientX - r.left)),
      y: Math.max(0, Math.min(r.height, t.clientY - r.top)),
    };
  };

  const startDrag = (e) => {
    e.preventDefault();
    setDrag(point(e));
    setCrop(null);
  };

  const moveDrag = (e) => {
    if (!drag) return;
    const p = point(e);
    setCrop({
      x: Math.min(drag.x, p.x),
      y: Math.min(drag.y, p.y),
      w: Math.abs(p.x - drag.x),
      h: Math.abs(p.y - drag.y),
    });
  };

  const endDrag = () => {
    setDrag(null);
    if (crop && (crop.w < 12 || crop.h < 12)) setCrop(null);
  };

  const save = async () => {
    const baked = bakedRef.current;
    if (!baked) return;
    setSaving(true);
    try {
      let out = baked;
      if (crop) {
        const canvas = canvasRef.current;
        const sx = baked.width / canvas.width;
        const sy = baked.height / canvas.height;
        out = document.createElement("canvas");
        out.width = Math.max(1, Math.round(crop.w * sx));
        out.height = Math.max(1, Math.round(crop.h * sy));
        out
          .getContext("2d")
          .drawImage(
            baked,
            Math.round(crop.x * sx),
            Math.round(crop.y * sy),
            out.width,
            out.height,
            0,
            0,
            out.width,
            out.height
          );
      }
      const blob = await new Promise((res) => out.toBlob(res, "image/jpeg", 0.92));
      const fd = new FormData();
      fd.append("file", new File([blob], "shade-edited.jpg", { type: "image/jpeg" }));
      const { data } = await api.post("/upload", fd);
      toast.success("Photo updated");
      onSaved(data.image_path);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save the edited photo");
    } finally {
      setSaving(false);
    }
  };

  const Tool = ({ testId, onClick, icon: Icon, children }) => (
    <Button
      type="button"
      data-testid={testId}
      variant="outline"
      size="sm"
      className="rounded-sm"
      onClick={onClick}
    >
      <Icon size={14} className="mr-1.5" /> {children}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="image-editor-dialog" className="max-w-2xl rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-head text-xl font-extrabold tracking-tighter">
            Rotate & crop photo
          </DialogTitle>
          <DialogDescription className="text-xs">
            Drag on the photo to pick the area you want to keep, then save.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          <Tool testId="image-rotate-left" icon={RotateCcw} onClick={() => setRotation((r) => (r + 270) % 360)}>
            Left
          </Tool>
          <Tool testId="image-rotate-right" icon={RotateCw} onClick={() => setRotation((r) => (r + 90) % 360)}>
            Right
          </Tool>
          <Tool testId="image-flip" icon={FlipHorizontal} onClick={() => setFlip((f) => !f)}>
            Flip
          </Tool>
          <Tool
            testId="image-reset"
            icon={Undo2}
            onClick={() => {
              setRotation(0);
              setFlip(false);
              setCrop(null);
            }}
          >
            Reset
          </Tool>
          {crop && (
            <Tool testId="image-clear-crop" icon={Crop} onClick={() => setCrop(null)}>
              Clear crop
            </Tool>
          )}
        </div>

        <div className="flex justify-center bg-zinc-100 p-3">
          <div
            ref={boxRef}
            data-testid="image-editor-canvas-box"
            className="relative touch-none select-none"
            onMouseDown={startDrag}
            onMouseMove={moveDrag}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onTouchStart={startDrag}
            onTouchMove={moveDrag}
            onTouchEnd={endDrag}
          >
            <canvas ref={canvasRef} className="block max-w-full cursor-crosshair" />
            {crop && (
              <div
                data-testid="image-crop-box"
                className="pointer-events-none absolute border-2 border-[#C8A03C] bg-[#C8A03C]/10"
                style={{ left: crop.x, top: crop.y, width: crop.w, height: crop.h }}
              />
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            data-testid="image-editor-cancel"
            variant="ghost"
            className="rounded-sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="image-editor-save"
            disabled={saving || !img}
            className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
            onClick={save}
          >
            {saving ? "Saving…" : "Save photo"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
